import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Button, 
  Card, 
  Typography,
  Form,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Table,
  Space,
  message,
  Divider,
  Popconfirm
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SendOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import quotesService, { Quote, QuoteItem } from '../../services/quotesService';
import { customersService, Customer } from '../../services/customersService';
import { serviceProduits, Produit } from '../../services/productsService';
import { formatCFA } from '../../utils/formatters';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const QuoteForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Produit[]>([]);
  const [items, setItems] = useState<QuoteItem[]>([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    discountAmount: 0,
    taxAmount: 0,
    total: 0
  });

  const isEditing = id && id !== 'nouveau';

  // Charger les données initiales
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        console.log('Chargement des données du formulaire de devis...');
        
        // Charger les clients
        try {
          const customersResponse = await customersService.getCustomers({ limit: 1000 });
          console.log('Réponse clients:', customersResponse);
          setCustomers(customersResponse.data.customers || []);
        } catch (customerError) {
          console.error('Erreur lors du chargement des clients:', customerError);
          message.warning('Impossible de charger les clients - Utilisation de données d\'exemple');
          // Données d'exemple pour les clients
          setCustomers([
            {
              id: 1,
              first_name: 'Jean',
              last_name: 'Dupont',
              user: { id: 1, email: 'jean.dupont@example.com' }
            },
            {
              id: 2, 
              first_name: 'Marie',
              last_name: 'Martin',
              user: { id: 2, email: 'marie.martin@example.com' }
            }
          ] as Customer[]);
        }

        // Charger les produits
        try {
          const productsResponse = await serviceProduits.listerProduits({ limit: 1000 });
          console.log('Réponse produits:', productsResponse);
          setProducts(productsResponse.data || []);
        } catch (productError) {
          console.error('Erreur lors du chargement des produits:', productError);
          message.warning('Impossible de charger les produits - Utilisation de données d\'exemple');
          // Données d'exemple pour les produits
          setProducts([
            {
              id: 1,
              nom: 'Climatiseur Split 12000 BTU',
              reference: 'CLM-12K-001',
              prix: 1200,
              description: 'Climatiseur split 12000 BTU haute efficacité'
            },
            {
              id: 2,
              nom: 'Pompe à chaleur 8kW', 
              reference: 'PAC-8K-002',
              prix: 3500,
              description: 'Pompe à chaleur air/eau 8kW'
            },
            {
              id: 3,
              nom: 'Maintenance préventive',
              reference: 'MAINT-PREV-001',
              prix: 150,
              description: 'Service de maintenance préventive'
            }
          ] as Produit[]);
        }

        // Si mode édition, charger le devis existant
        if (isEditing) {
          try {
            const quoteData = await quotesService.getQuoteById(parseInt(id));
            console.log('Données du devis:', quoteData);
            setQuote(quoteData);
            
            // S'assurer que le champ isCustom est défini pour chaque item
            const loadedItems = (quoteData.items || []).map((item: QuoteItem) => ({
              ...item,
              isCustom: item.isCustom || item.productId === -1 || item.productId < 0
            }));
            setItems(loadedItems);
            
            // Pré-remplir le formulaire
            form.setFieldsValue({
              customerId: quoteData.customerId,
              issueDate: dayjs(quoteData.issueDate),
              expiryDate: dayjs(quoteData.expiryDate),
              notes: quoteData.notes,
              termsAndConditions: quoteData.termsAndConditions,
              objet: quoteData.objet || "Devis d'entretien de climatisation",
            });
          } catch (quoteError) {
            console.error('Erreur lors du chargement du devis:', quoteError);
            message.error('Impossible de charger le devis');
            navigate('/devis');
            return;
          }
        } else {
          // Nouveau devis - définir les dates par défaut
          const today = dayjs();
          const expiryDate = dayjs().add(30, 'days');
          
          form.setFieldsValue({
            issueDate: today,
            expiryDate: expiryDate,
            objet: "Devis d'entretien de climatisation",
          });
        }
      } catch (error) {
        console.error('Erreur générale lors du chargement des données:', error);
        message.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [id, isEditing, form, navigate]);

  // Calculer les totaux quand les items changent
  useEffect(() => {
    const calculateTotals = () => {
      const subtotal = items.reduce((sum, item) => {
        const itemTotal = item.quantity * item.unitPrice * (1 - item.discount / 100);
        return sum + itemTotal;
      }, 0);

      const discountAmount = items.reduce((sum, item) => {
        const discountValue = item.quantity * item.unitPrice * (item.discount / 100);
        return sum + discountValue;
      }, 0);

      const taxAmount = items.reduce((sum, item) => {
        const itemTotal = item.quantity * item.unitPrice * (1 - item.discount / 100);
        const tax = itemTotal * (item.taxRate / 100);
        return sum + tax;
      }, 0);

      const total = subtotal + taxAmount;

      setTotals({
        subtotal,
        discountAmount,
        taxAmount,
        total
      });
    };

    calculateTotals();
  }, [items]);

  // Ajouter un nouvel item (produit du catalogue)
  const addItem = () => {
    const newItem: QuoteItem = {
      productId: 0,
      productName: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      taxRate: 0, // TVA par défaut à 0%
      isCustom: false
    };
    setItems([...items, newItem]);
  };

  // Ajouter un article personnalisé
  const addCustomItem = () => {
    const newItem: QuoteItem = {
      productId: -1, // ID négatif pour les articles personnalisés
      productName: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      taxRate: 0, // TVA par défaut à 0%
      isCustom: true
    };
    setItems([...items, newItem]);
  };

  // Supprimer un item
  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // Mettre à jour un item
  const updateItem = (index: number, field: keyof QuoteItem, value: any) => {
    const newItems = [...items];
    
    if (field === 'productId') {
      const selectedProduct = products.find(p => p.id === value);
      if (selectedProduct) {
        newItems[index] = {
          ...newItems[index],
          productId: value,
          productName: selectedProduct.nom,
          unitPrice: selectedProduct.prix || 0
        };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    
    setItems(newItems);
  };

  // Sauvegarder le devis
  const handleSave = async (status: Quote['status'] = 'draft') => {
    try {
      setSaving(true);
      const formValues = await form.validateFields();
      
      if (items.length === 0) {
        message.error('Veuillez ajouter au moins un produit au devis');
        return;
      }

      const quoteData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'> = {
        reference: quote?.reference || (() => {
          const d = new Date();
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const year = d.getFullYear();
          const randomSuffix = Math.floor(100 + Math.random() * 900);
          return `DEV-${day}${month}${year}-${randomSuffix}`;
        })(),
        customerId: formValues.customerId,
        customerName: (customers.find(c => c.id === formValues.customerId)?.first_name || '') + ' ' + 
                     (customers.find(c => c.id === formValues.customerId)?.last_name || ''),
        issueDate: formValues.issueDate.format('YYYY-MM-DD'),
        expiryDate: formValues.expiryDate.format('YYYY-MM-DD'),
        status,
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        discountAmount: totals.discountAmount,
        total: totals.total,
        notes: formValues.notes,
        termsAndConditions: formValues.termsAndConditions,
        objet: formValues.objet,
        items: items.filter(item => {
          // Garder les items du catalogue avec produit sélectionné
          if (!item.isCustom && item.productId > 0) return true;
          // Garder les items personnalisés avec un nom
          if (item.isCustom && item.productName.trim() !== '') return true;
          return false;
        })
      };

      if (isEditing) {
        await quotesService.updateQuote(parseInt(id), quoteData);
      } else {
        await quotesService.createQuote(quoteData);
      }

      message.success(`Devis ${isEditing ? 'modifié' : 'créé'} avec succès`);
      navigate('/devis');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde du devis');
    } finally {
      setSaving(false);
    }
  };

  // Configuration des colonnes du tableau des items
  const itemColumns: ColumnsType<QuoteItem> = [
    {
      title: 'Produit / Description',
      dataIndex: 'productId',
      width: 280,
      render: (value, record, index) => {
        // Article personnalisé : champ texte libre
        // Vérifier isCustom OU si productId est négatif (pour les anciens devis)
        if (record.isCustom || record.productId < 0) {
          return (
            <Input
              style={{ width: '100%' }}
              placeholder="Ex: Main d'œuvre, Déplacement, Service spécifique..."
              value={record.productName}
              onChange={(e) => {
                const newItems = [...items];
                newItems[index] = { ...newItems[index], productName: e.target.value };
                setItems(newItems);
              }}
              prefix={<span style={{ color: '#1890ff' }}>✎</span>}
            />
          );
        }
        
        // Produit du catalogue : dropdown
        return (
          <Select
            style={{ width: '100%' }}
            placeholder="Sélectionner un produit"
            value={value > 0 ? value : undefined}
            onChange={(productId) => updateItem(index, 'productId', productId)}
            showSearch
            filterOption={(input, option) =>
              option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
            }
          >
            {products.map(product => (
              <Option key={product.id} value={product.id}>
                {product.nom} - {product.reference}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: 'Quantité',
      dataIndex: 'quantity',
      width: 100,
      render: (value, record, index) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(quantity) => updateItem(index, 'quantity', quantity || 1)}
        />
      ),
    },
    {
      title: 'Prix unitaire (F CFA)',
      dataIndex: 'unitPrice',
      width: 140,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          step={1000}
          value={value}
          onChange={(price) => updateItem(index, 'unitPrice', price || 0)}
          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          disabled={!record.isCustom && record.productId <= 0}
          placeholder={(record.isCustom || record.productId < 0) ? "Prix" : "Auto"}
        />
      ),
    },
    {
      title: 'Remise (%)',
      dataIndex: 'discount',
      width: 100,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          max={100}
          step={0.1}
          value={value}
          onChange={(discount) => updateItem(index, 'discount', discount || 0)}
          formatter={value => `${value}%`}
        />
      ),
    },
    {
      title: 'TVA (%)',
      dataIndex: 'taxRate',
      width: 100,
      render: (value, record, index) => (
        <InputNumber
          min={0}
          max={100}
          step={0.1}
          value={value}
          onChange={(taxRate) => updateItem(index, 'taxRate', taxRate ?? 0)}
          formatter={value => `${value}%`}
        />
      ),
    },
    {
      title: 'Total HT (F CFA)',
      dataIndex: 'total',
      width: 120,
      render: (_, record) => formatCFA(record.unitPrice * record.quantity),
    },
    {
      title: 'Actions',
      width: 80,
      render: (_, record, index) => (
        <Popconfirm
          title="Supprimer cet item"
          description="Êtes-vous sûr de vouloir supprimer cet item ?"
          onConfirm={() => removeItem(index)}
          okText="Oui"
          cancelText="Non"
        >
          <Button 
            type="text" 
            icon={<DeleteOutlined />} 
            size="small"
            danger
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* En-tête */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/devis')}
            style={{ marginRight: 16 }}
          >
            Retour
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isEditing ? 'Modifier le devis' : 'Nouveau devis'}
            </Title>
            {quote && (
              <Text type="secondary">Référence: {quote.reference}</Text>
            )}
          </div>
        </div>
        <Space>
          <Button
            icon={<SaveOutlined />}
            onClick={() => handleSave('draft')}
            loading={saving}
            size="large"
          >
            Enregistrer en brouillon
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleSave('sent')}
            loading={saving}
            size="large"
          >
            Enregistrer et envoyer
          </Button>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        disabled={loading}
      >
        <Row gutter={[24, 24]}>
          {/* Informations générales */}
          <Col xs={24} lg={12}>
            <Card title="Informations générales" style={{ borderRadius: '8px' }}>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name="customerId"
                    label="Client"
                    rules={[{ required: true, message: 'Veuillez sélectionner un client' }]}
                  >
                    <Select
                      placeholder="Sélectionner un client"
                      showSearch
                      filterOption={(input, option) =>
                        option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                      }
                    >
                      {customers.map(customer => (
                        <Option key={customer.id} value={customer.id}>
                           {customer.first_name} {customer.last_name}
                           {customer.email && ` - ${customer.email}`}
                           {customer.phone && ` - ${customer.phone}`}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="objet"
                    label="Objet du Devis"
                    rules={[{ required: true, message: 'Veuillez saisir l\'objet du devis' }]}
                  >
                    <Input placeholder="ex: Devis d'entretien de climatisation" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="issueDate"
                    label="Date d'émission"
                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="expiryDate"
                    label="Date d'expiration"
                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Résumé financier */}
          <Col xs={24} lg={12}>
            <Card title="Résumé financier" style={{ borderRadius: '8px' }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Text strong>Sous-total HT:</Text>
                </Col>
                <Col span={12}>
                  <Text>{formatCFA(totals.subtotal)}</Text>
                </Col>
                <Col span={12}>
                  <Text>Remise totale:</Text>
                </Col>
                <Col span={12}>
                  <Text type="success">-{formatCFA(totals.discountAmount)}</Text>
                </Col>
                <Col span={12}>
                  <Text>TVA:</Text>
                </Col>
                <Col span={12}>
                  <Text>{formatCFA(totals.taxAmount)}</Text>
                </Col>
                <Divider style={{ margin: '8px 0' }} />
                <Col span={12}>
                  <Text strong style={{ fontSize: '16px' }}>Total TTC:</Text>
                </Col>
                <Col span={12}>
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    {formatCFA(totals.total)}
                  </Text>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        {/* Tableau des produits */}
        <Card 
          title="Produits et services" 
          style={{ marginTop: '24px', borderRadius: '8px' }}
          extra={(
            <Space>
              <Button 
                type="default" 
                icon={<PlusOutlined />} 
                onClick={addItem}
              >
                Produit du catalogue
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={addCustomItem}
                style={{ 
                  background: '#52c41a',
                  borderColor: '#52c41a'
                }}
              >
                Article personnalisé
              </Button>
            </Space>
          )}
        >
          <Table
            dataSource={items}
            columns={itemColumns}
            rowKey={(record, index) => index?.toString() || '0'}
            pagination={false}
            scroll={{ x: 800 }}
            locale={{
              emptyText: 'Aucun produit ajouté. Utilisez les boutons ci-dessus pour ajouter un produit du catalogue ou un article personnalisé.'
            }}
          />
        </Card>

        {/* Notes et conditions */}
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24} lg={12}>
            <Card title="Notes" style={{ borderRadius: '8px' }}>
              <Form.Item name="notes">
                <TextArea
                  rows={4}
                  placeholder="Notes internes ou commentaires pour le client..."
                />
              </Form.Item>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Conditions générales" style={{ borderRadius: '8px' }}>
              <Form.Item name="termsAndConditions">
                <TextArea
                  rows={4}
                  placeholder="Conditions de paiement, garanties, délais..."
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default QuoteForm;